import { Animated, NativeScrollEvent, NativeSyntheticEvent, Platform } from 'react-native';
import { ScrollHandlerProcessed, useAnimatedScrollHandler } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Route } from 'react-native-tab-view';

import { CollapsibleContext, useCollapsibleContext } from '@/contexts/collapsibleTabViewContext';

type CollapsibleScenePropsAndRef = {
  /**
   * Disable scroll for unfocused routes is optional,
   * but prevents weird/delayed animations if the user changes tabs
   * and quickly start scrolling the new tab, before
   * the scrollY starting to track the new focused route.
   */
  scrollEnabled: boolean;
  /**
   * Scroll event, enabled only for the focused route.
   */
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollHandler?: ScrollHandlerProcessed<Record<string, unknown>>;
  scrollTopHeader: () => void;

  /**
   * Content container style with `paddingTop` and `minHeight`.
   */
  contentContainerStyle: {
    flexGrow: number;
    paddingTop: number;
    minHeight: number;
  };
  headerHeight: number;
  /**
   * Needed for the loading indicator to show correctly on android.
   */
  progressViewOffset: number;
  /**
   * For use in the Animated ListEmptyComponent.
   */
  tabBarHeight: number;
} & Pick<CollapsibleContext, 'onMomentumScrollBegin' | 'onScrollEndDrag' | 'onMomentumScrollEnd'>;

const useCollapsibleScene = <T extends Route>(routeKey: T['key']): CollapsibleScenePropsAndRef => {
  const context = useCollapsibleContext();

  const {
    activeRouteKey,
    scrollY,
    minHeight = 0,
    headerHeight = 0,
    tabBarHeight = 0,
    containerHeight,
    scrollOffset,
    maxHeaderHeight,
    ...rest
  } = context;

  const scrollEnabled = routeKey === activeRouteKey;
  const topInset = useSafeAreaInsets().top;

  const scrollHandler = useAnimatedScrollHandler((event: NativeScrollEvent) => {
    if (scrollEnabled && scrollOffset && maxHeaderHeight && event.contentOffset.y < maxHeaderHeight) {
      scrollOffset.value = event.contentOffset.y;
    }
  });

  const onScroll =
    scrollEnabled && scrollY
      ? Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: Platform.OS === 'web' ? false : true
        })
      : undefined;

  const scrollTopHeader = () => {
    if (scrollY) {
      scrollY.setValue(0);
    }
    if (scrollOffset) {
      scrollOffset.value = 0;
    }
  };

  return {
    scrollEnabled,
    onScroll,
    scrollTopHeader,
    scrollHandler,
    contentContainerStyle: {
      flexGrow: 1,
      paddingTop: headerHeight + tabBarHeight + topInset,
      minHeight: minHeight + headerHeight
    },
    headerHeight,
    progressViewOffset: headerHeight + tabBarHeight,
    tabBarHeight,
    ...rest
  };
};

export default useCollapsibleScene;
